import type { ListingTemplateProduct } from "@/lib/listing-templates";

/**
 * Shared three-part listing-template footer.
 * LEFT brand/context · CENTER subtle Tradivo attribution · RIGHT SKU.
 * Templates supply their own colours; layout stays left/centre/right on desktop
 * and stacks cleanly on narrow viewports.
 */
export function TemplatePoweredFooter({
  left,
  sku,
  className = "",
  leftClassName = "",
  centerClassName = "text-center opacity-80",
  rightClassName = "sm:text-right",
  testId = "template-powered-footer",
}: {
  left: string;
  sku?: string | null;
  className?: string;
  leftClassName?: string;
  centerClassName?: string;
  rightClassName?: string;
  testId?: string;
}) {
  const trimmedSku = typeof sku === "string" ? sku.trim() : "";
  return (
    <div
      data-testid={testId}
      className={`grid grid-cols-1 sm:grid-cols-3 items-center gap-x-4 gap-y-2 text-xs ${className}`}
    >
      <span className={`min-w-0 break-words ${leftClassName}`}>{left}</span>
      <span className={centerClassName}>Powered by Tradivo</span>
      {trimmedSku ? (
        <span className={`min-w-0 break-all ${rightClassName}`}>SKU {trimmedSku}</span>
      ) : (
        <span className="hidden sm:block" aria-hidden="true" />
      )}
    </div>
  );
}

/** A compact 5-star rating rendered from a 0–5 score. */
export function Stars({ score, count, className = "" }: { score: number; count?: number; className?: string }) {
  const full = Math.round(score);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className="flex text-amber-500 text-sm" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <i key={i} className={i <= full ? "ri-star-fill" : "ri-star-line"} />
        ))}
      </span>
      <span className="text-sm text-slate-500">{score.toFixed(1)}</span>
      {typeof count === "number" && <span className="text-sm text-slate-400">({count})</span>}
    </span>
  );
}

/** A pill-style variant selector (colour / size) — presentational only. */
export function VariantPills({ product }: { product: ListingTemplateProduct }) {
  if (!product.variants || product.variants.length === 0) return null;
  return (
    <div className="space-y-3">
      {product.variants.map((variant) => (
        <div key={variant.name}>
          <p className="text-sm font-medium text-slate-800 mb-1.5">{variant.name}</p>
          <div className="flex flex-wrap gap-2">
            {variant.options.map((option, i) => (
              <button
                key={option}
                type="button"
                className={`px-3 py-1.5 text-sm rounded-full border ${
                  i === 0
                    ? "border-emerald-600 bg-emerald-50 text-emerald-700 font-medium"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
